import React, { useEffect, useState } from "react";

import { Button, Col, Divider, Empty, Flex, Form, Input, InputNumber, notification, Row, Select, SelectProps, Space, Spin, Tooltip, Typography } from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "antd/lib/form/Form";
import { QuestionCircleTwoTone } from "@ant-design/icons";

import { EnderecoService } from "../../../../../Services/EnderecoService";
import { EnumTipoDeDocumento } from "../../../../../Models/Domain/Pessoa/Enum";
import { Formatacoes } from "../../../../../Utils/Formatacao";
import { InputComReactIMask } from "../../../../../Components/InputComReactIMask";
import { InputTelefoneECelular } from "../../../../../Components/InputTelefoneFixoECelular";
import { Ordenar } from "../../../../../Utils/Ordenacao";
import { PropriedadesDeNotificacao } from "../../../../../Utils/Notificacoes";
import { ResponsavelService } from "../../../../../Services/Domain/Pessoa/ResponsavelService";
import { SelectComPesquisa } from "../../../../../Components/SelectComPesquisa";
import { TypeEmail } from "../../../../../Models/Pessoa/Contato/Email";
import { TypeEndereco } from "../../../../../Models/Pessoa/Endereco";
import { TypeResponsavel } from "../../../../../Models/Domain/Pessoa/Responsavel";
import { TypeTelefone } from "../../../../../Models/Pessoa/Contato/Telefone";
import { EnumTipoUsuario } from "../../../../../Models/Domain/AreaRestrita/Sessao";
import { RegrasComunsDeValidacaoDaAR } from "../../../../../AreaRestritta/Utils/RegrasComunsDeValidacaoDaAR";

interface IFormularioDoCadastroDeResponsavel {
    responsavel: TypeResponsavel,
    telefone: TypeTelefone,
    endereco: TypeEndereco,
    email: TypeEmail,
}

export const CadastroDeResponsavel: React.FC<
    ({privilegio: "administrador", responsavelEmAlteracao?: TypeResponsavel } | {privilegio: "responsavel", responsavelEmAlteracao: TypeResponsavel }) & 
    { 
        middleware?: (t: TypeResponsavel, v: IFormularioDoCadastroDeResponsavel) => TypeResponsavel,
        aoCancelar?: () => any,
        depoisDeFinalizar?: (responsavelInserido?: TypeResponsavel) => any,
        txtSalvar?: string,
        txtAtualizar?: string,
        limparFormularioAoFinalizar?: boolean,
        notificar?: boolean
    }
> = (props) => {
    
    const [ form ] = useForm<IFormularioDoCadastroDeResponsavel>()
    const valoresDoFormulario = useWatch([], form);

    const estadosDisponiveis = useQuery({
        queryKey: ["listarEstados"],
        queryFn: () => new EnderecoService().listarEstados()
    })

    const [configurouAsDependenciasDeEdicao, setConfigurouAsDependenciasDeEdicao] = useState<boolean>(false);
    useEffect(() => {
        if(props.responsavelEmAlteracao == undefined) return;

        const endereco = props.responsavelEmAlteracao.pessoa?.enderecos?.at(0);
        const email = props.responsavelEmAlteracao.pessoa?.emails?.find((e) => e.principal == true) ?? props.responsavelEmAlteracao.pessoa?.emails?.at(0);
        const telefone = props.responsavelEmAlteracao.pessoa?.telefones?.find((t) => t.principal == true) ?? props.responsavelEmAlteracao.pessoa?.telefones?.at(0);

        form.setFieldsValue({
            responsavel: props.responsavelEmAlteracao,
            endereco: endereco,
            email: email,
            telefone: telefone
        })

        if(endereco?.cep != undefined){
            form.validateFields([["endereco", "cep"]])
        }

        setConfigurouAsDependenciasDeEdicao(true);
    }, [])

    const municipiosDisponiveis = useQuery({
        enabled: valoresDoFormulario?.endereco?.estado?.codigo != undefined,
        queryKey: [`listarMunicipios/${valoresDoFormulario?.endereco.estado.codigo}`, valoresDoFormulario?.endereco.estado.codigo],
        queryFn: () => new EnderecoService().listarMunicipios({ codigo: valoresDoFormulario?.endereco.estado.codigo })
    })

    const [enderecoRecebidoDoViaCEP, setEnderecoRecebidoDoViaCEP] = useState<Partial<TypeEndereco>>({});

    useEffect(() => {
        if ( Object.entries((enderecoRecebidoDoViaCEP)).map(([k, v]) => v).every((v) => v == undefined)) return;
        
        form.setFieldsValue({
            endereco: {
                ...form.getFieldValue(["endereco"]),
                ...enderecoRecebidoDoViaCEP
            }
        })

        form.validateFields([["endereco", "estado"]], { recursive: true })
        form.validateFields([["endereco", "municipio"]], { recursive: true })
        form.validateFields([["endereco", "logradouro"]], { recursive: true })
        form.validateFields([["endereco", "bairro"]], { recursive: true })

    }, [enderecoRecebidoDoViaCEP])

    const inserirResponsavel = useMutation({
        mutationFn: async (payload: TypeResponsavel) => {
            return new ResponsavelService().inserir(payload)
        },
        onSuccess: (response) => {
            if (props.notificar !== false) {
                let mensagem: JSX.Element = <> Cadastro realizado com sucesso. </>
                notification.open({
                    ...PropriedadesDeNotificacao.Sucesso(mensagem)
                })
            }

            if (props.limparFormularioAoFinalizar !== false) {
                setEnderecoRecebidoDoViaCEP({});
                form.resetFields();
            }

            const responsavelInserido: TypeResponsavel = response.data;
            props.depoisDeFinalizar?.(responsavelInserido);
        }
    })

    const atualizarResponsavel = useMutation({
        mutationFn: async (payload: TypeResponsavel) => {
            return new ResponsavelService().atualizar(payload)
        },
        onSuccess: () => {
            if(props.notificar !== false){
                let mensagem: JSX.Element = <> Cadastro atualizado com sucesso. </>

                notification.open({
                    ...PropriedadesDeNotificacao.Sucesso(mensagem)
                })
            }

            if(props.limparFormularioAoFinalizar !== false){
                setEnderecoRecebidoDoViaCEP({});
                form.resetFields();
            }

            props.depoisDeFinalizar?.();
        }
    })

    const aoFinalizar = (valores: IFormularioDoCadastroDeResponsavel) => {
        const estaEditando = props.responsavelEmAlteracao != undefined;

        let payload: TypeResponsavel = {
            ...valores.responsavel,
            pessoa: {
                ...valores.responsavel.pessoa,
                cpf: Formatacoes.somenteDigitos(valores.responsavel.pessoa?.cpf),
                emails: valores.email ? [valores.email] : undefined,
                telefones: valores.telefone && valores.telefone.numero ? [{ ...valores.telefone, numero: Formatacoes.sanitizar("-", valores.telefone.numero?.trim()) }] : undefined,
                enderecos: valores.endereco && valores.endereco.cep ? [valores.endereco] : undefined,
            },
        }

        if(estaEditando == false){
            payload = {
                ...payload,
                usuarioDaAreaRestrita: {
                    email: valores.email.endereco,
                    senha: (valores.responsavel.usuarioDaAreaRestrita?.senha ?? "").trim().length == 0 ? undefined : valores.responsavel.usuarioDaAreaRestrita?.senha ?? ""
                }
            }

            if(props.middleware) payload = props.middleware(payload, valores);

            inserirResponsavel.mutate(payload);
        } else {
            payload = {
                ...payload,
                codigo: props.responsavelEmAlteracao?.codigo,
                ativo: !!props.responsavelEmAlteracao?.ativo,
            }

            if(props.middleware) payload = props.middleware(payload, valores);

            atualizarResponsavel.mutate(payload);
        }
    }

    if(props.privilegio == "responsavel" && props.responsavelEmAlteracao == undefined) return <Empty description="Responsáveis não podem inserir novos responsáveis"/>

    return <React.Fragment>
        <Spin spinning={props.responsavelEmAlteracao != undefined && configurouAsDependenciasDeEdicao == false}>
            <Form<IFormularioDoCadastroDeResponsavel> 
                form={form}
                onFinish={aoFinalizar}
                onFinishFailed={(info) => {
                    const mensagem = <>O formulário não pôde ser submetido.</>;
                    notification.open({
                        ...PropriedadesDeNotificacao.Erro(mensagem)
                    })
                }}
                layout="vertical"
                validateTrigger="onBlur"
                autoComplete="off"
            >
                <Row gutter={[16, 8]}>
                    <Col xs={24} lg={12}>
                        <Form.Item<IFormularioDoCadastroDeResponsavel>
                            name={["responsavel", "pessoa", "nome"]}
                            label="Nome"
                            rules={[{ required: true, message: "Por favor, informe o nome do responsável!" }]}
                        >
                            <Input placeholder="Nome do responsável" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} lg={12}>
                        <Tooltip 
                            title={props.privilegio === "responsavel" ? "Contate um administrador caso deseje alterar seu CPF" : undefined}
                            placement="bottomLeft"
                        >
                            <Form.Item<IFormularioDoCadastroDeResponsavel>
                                name={["responsavel", "pessoa", "cpf"]}
                                label="CPF"
                                rules={[
                                    { required: true, message: "Por favor, informe o CPF do responsável!" },
                                    { pattern: /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/, message: "Por favor, insira um CPF válido!" }
                                ]}
                            >
                                <InputComReactIMask
                                    disabled={!((props?.responsavelEmAlteracao?.pessoa?.cpf ?? "").trim().length == 0 || props.privilegio === "administrador")}
                                    maskOptions={{
                                        mask: "000.000.000-00"
                                    }}
                                    placeholder="000.000.000-00"
                                />
                            </Form.Item>
                        </Tooltip>
                    </Col>

                    <Col lg={4}>
                        <Form.Item<IFormularioDoCadastroDeResponsavel>
                            name={["responsavel", "tipoDeDocumento"]}
                            label="Tipo de documento"
                            rules={[{ required: false }]}
                        >
                            <Select 
                                allowClear
                                placeholder="Selecione"
                                onChange={(value) => {
                                    if(value == undefined){
                                        form.setFieldValue(["responsavel", "documento"], undefined)
                                    }
                                }}
                                options={[
                                    {
                                        value: EnumTipoDeDocumento.RG,
                                        label: "RG",
                                    },
                                    {
                                        value: EnumTipoDeDocumento.RNE,
                                        label: "RNE",
                                    },
                                    {
                                        value: EnumTipoDeDocumento.PASSAPORTE,
                                        label: "Passaporte",
                                    },
                                ]}
                            />
                        </Form.Item>
                    </Col>

                    <Col lg={4}>
                        <Form.Item<IFormularioDoCadastroDeResponsavel>
                            name={["responsavel", "documento"]}
                            label={
                                valoresDoFormulario?.responsavel.tipoDeDocumento == EnumTipoDeDocumento.RG ?
                                    "RG" :
                                valoresDoFormulario?.responsavel.tipoDeDocumento == EnumTipoDeDocumento.RNE ?
                                    "RNE" :
                                    valoresDoFormulario?.responsavel.tipoDeDocumento == EnumTipoDeDocumento.PASSAPORTE ?
                                    "Passaporte" :
                                "Documento" 
                            }
                            dependencies={[["responsavel", "tipoDeDocumento"]]}
                            rules={[{ required: valoresDoFormulario?.responsavel.tipoDeDocumento != undefined, message: "O campo não pode ser vazio!" }]}
                        >
                            <Input 
                                disabled={valoresDoFormulario?.responsavel.tipoDeDocumento == undefined} 
                                placeholder={
                                    valoresDoFormulario?.responsavel.tipoDeDocumento == EnumTipoDeDocumento.RG?
                                        "Insira o RG" :
                                    valoresDoFormulario?.responsavel.tipoDeDocumento == EnumTipoDeDocumento.RNE?
                                        "Insira o RNE" :
                                    valoresDoFormulario?.responsavel.tipoDeDocumento == EnumTipoDeDocumento.PASSAPORTE?
                                        "Insira o passaporte" :

                                    "Valor do documento" 
                                } 
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider style={{margin: "1em"}}>Endereço</Divider>

                <Row gutter={[ 16, 8 ]}>
                    <Col xs={24} sm={12} lg={8}>
                        <Form.Item<IFormularioDoCadastroDeResponsavel>
                            name={["endereco", "cep"]}
                            validateTrigger="onBlur"
                            label={<><Typography>CEP <Tooltip title={"O CEP pode ser utilizado para preencher parte do endereço automaticamente."}><QuestionCircleTwoTone /> </Tooltip>  </Typography> </>}
                            rules={[
                                {
                                    required: true,
                                    message: "O CEP é obrigatório"
                                },
                                {
                                    validator: async (rules, cep) => {
                                        setEnderecoRecebidoDoViaCEP({});

                                        if(!cep)
                                            return Promise.resolve();

                                        const pattern = /^\d{5}-\d{3}$/;
                                        if (cep == undefined) return Promise.reject("O CEP é obrigatório!");
                                        if (pattern.test(cep) == false) return Promise.reject("Insira um CEP no formato válido!");

                                        try {
                                            const informacoesDoEnderecoPeloViaCEP = await new EnderecoService().obterInformacoesDoCEP(cep);
                                            const endereco = await Formatacoes.viaCepParaEndereco(informacoesDoEnderecoPeloViaCEP, estadosDisponiveis.data?.data);

                                            setEnderecoRecebidoDoViaCEP(endereco);
                                            return Promise.resolve();

                                        } catch (error) {
                                            return Promise.reject(error);
                                        }
                                    }
                                }
                            ]}
                        >
                            <InputComReactIMask
                                maskOptions={{
                                    mask: "00000-000"
                                }}
                                placeholder="00000-000"
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} lg={8}>
                        <Form.Item<IFormularioDoCadastroDeResponsavel>
                            name={["endereco", "estado", "codigo"]}
                            label="Estado"
                            rules={[{ required: false, message: "Por favor, selecione o estado!" }]}
                        >
                            <SelectComPesquisa
                                allowClear
                                placeholder="Selecione o estado" 
                                loading={estadosDisponiveis.isFetching}
                                disabled={enderecoRecebidoDoViaCEP.estado?.codigo != undefined}
                                options={estadosDisponiveis.data?.data.sort((a, b) => Ordenar.String(a.nome, b.nome)).map((item) => ({
                                    value: item.codigo,
                                    label: item.nome,
                                    sigla: item.sigla
                                }))}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} lg={8}>
                        <Form.Item<IFormularioDoCadastroDeResponsavel>
                            name={["endereco", "municipio", "codigo"]}
                            label="Município"
                            rules={[{ required: false, message: "Por favor, selecione o município!" }]}
                        >
                            <SelectComPesquisa
                                allowClear
                                placeholder="Selecione o município" 
                                disabled={enderecoRecebidoDoViaCEP.municipio?.codigo != undefined || valoresDoFormulario?.endereco?.estado.codigo == undefined } 
                                loading={municipiosDisponiveis.isFetching}
                                options={municipiosDisponiveis.data?.data.sort((a, b) => Ordenar.String(a.nome, b.nome)).map((item) => ({
                                    value: item.codigo,
                                    label: item.nome,
                                }))}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} lg={8}>
                        <Form.Item<IFormularioDoCadastroDeResponsavel>
                            name={["endereco", "logradouro"]}
                            label="Logradouro"
                            rules={[{ required: false, message: "Por favor, informe o logradouro!" }]}
                        >
                            <Input disabled={enderecoRecebidoDoViaCEP.logradouro != undefined} placeholder="Logradouro" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} lg={4}>
                        <Form.Item<IFormularioDoCadastroDeResponsavel>
                            name={["endereco", "numero"]}
                            label="Número"
                            rules={[{ required: false, message: "Por favor, informe o número!" }]}
                        >
                            <Input placeholder="Número" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} lg={4}>
                        <Form.Item<IFormularioDoCadastroDeResponsavel>
                            name={["endereco", "bairro"]}
                            label="Bairro"
                            rules={[{ required: false, message: "Por favor, informe o bairro!" }]}
                        >
                            <Input disabled={enderecoRecebidoDoViaCEP.bairro != undefined} placeholder="Bairro" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Form.Item<IFormularioDoCadastroDeResponsavel>
                            name={["endereco", "complemento"]}
                            label="Complemento"
                        >
                            <Input placeholder="Complemento (opcional)" />
                        </Form.Item>
                    </Col>

                </Row>

                <Divider style={{margin: "1em"}}>Contatos</Divider>

                <Row gutter={[16, 8]}>
                    <Col xs={24} lg={12}>
                        <Form.Item<IFormularioDoCadastroDeResponsavel>
                            name={["email", "endereco"]}
                            label="E-mail"
                            rules={[
                                { required: true, message: "Por favor, informe o e-mail do responsável!" },
                                { type: "email", message: "Por favor, insira um e-mail válido!" }
                            ]}
                        >
                            <Input placeholder="E-mail do responsável" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Space.Compact>
                            <Form.Item<IFormularioDoCadastroDeResponsavel>
                                label={"DDD"}
                                name={["telefone", "ddd"]}
                                dependencies={["telefone", "numero"]}
                                style={{ width: "30%" }}
                                rules={[{ 
                                    required: valoresDoFormulario?.telefone?.numero != undefined && valoresDoFormulario.telefone.numero.trim() != "",
                                    message: 'Por favor, informe o DDD!', pattern: /^\d{2}$/, }]
                                }
                            >
                                <InputNumber controls={false} min={11} max={99} placeholder="DDD" />
                            </Form.Item>

                            <Form.Item<IFormularioDoCadastroDeResponsavel> 
                                label={"Telefone ou celular"}
                                name={["telefone", "numero"]}
                                dependencies={["telefone", "ddd"]}
                                style={{ width: "70%" }}
                                rules={[
                                    {
                                        required: valoresDoFormulario?.telefone?.ddd != undefined,
                                        message: 'Por favor, informe o número!'
                                    },
                                    {
                                        pattern: /^[0-9]{4,5}-?[0-9]{4}$/,
                                        message: "Formato inválido! Use XXXXX-XXXX ou XXXX-XXXX.",
                                    }
                                ]}
                            >
                                <InputTelefoneECelular />
                            </Form.Item>
                        </Space.Compact>
                    </Col>

                    {
                        (props.responsavelEmAlteracao ?? null) === null && <Col xs={24} md={12} lg={12} xl={6}>
                            <Form.Item<IFormularioDoCadastroDeResponsavel>
                                name={["responsavel", "usuarioDaAreaRestrita", "senha"]}
                                label="Senha"
                                rules={[
                                    ...RegrasComunsDeValidacaoDaAR.Senha
                                ]}
                            >
                                <Input.Password />
                            </Form.Item>
                        </Col>
                    }
                    
                </Row>

                <Row gutter={16} >
                    <Col xs={24}>
                        <Flex align="center" justify="center" gap={16} >
                            {props.aoCancelar &&
                                <Button danger type="primary" key={"cancel"} onClick={() => { props.aoCancelar && props.aoCancelar() }}>
                                    Cancelar
                                </Button>
                            }

                            <Button disabled={inserirResponsavel.isPending || atualizarResponsavel.isPending } onClick={form.submit} type="primary" key={"submit"}>
                                {/* {props.responsavelEmAlteracao == undefined? props.txtSalvar ?? "Salvar" : props.txtSalvar ?? "Atualizar"} */}
                                Salvar
                            </Button>
                        </Flex>
                    </Col>
                </Row>
            </Form>
        </Spin>
    </React.Fragment>
}